import { apiRequest } from './api';

/**
 * Opening hours API.
 *
 * Thin wrappers over `apiRequest` so components never assemble URLs or auth
 * headers themselves — the same shape as `services/npcs.js`.
 *
 * EVERY ROUTE HERE IS ADMIN-ONLY, enforced by `adminAuth` on the server. What
 * the page chooses to render is a display decision, never a security one.
 */

/**
 * Every building on a world, with whatever hours it has.
 *
 * BUILDINGS, NOT RECORDS. A building nobody has authored hours for is the
 * normal case, and it comes back with empty hours and `openNow: true` rather
 * than being missing from the list.
 *
 * `openNow` is the server's answer, not one computed here. Reimplementing the
 * predicate in the browser would be a second copy of rules that already have
 * one, and the copy is the one that goes stale.
 */
export const listBusinesses = (token, mapName) => apiRequest(`/api/businesses/${mapName}`, { token });

export const saveHours = (token, mapName, zoneId, body) => apiRequest(
  `/api/businesses/${mapName}/${zoneId}`,
  { method: 'PUT', token, body },
);

/**
 * Shutting a place is its own call, not a field on `saveHours`.
 *
 * The server refuses `closure` on PUT for the same reason publishing an NPC is
 * separate from saving one: it is the change everybody sees immediately, so it
 * should be a deliberate act rather than a side effect of saving a form. It also
 * means a stale form in another tab cannot reopen a shop somebody just shut.
 *
 * `closedUntil` is an ISO instant — an absolute moment, unlike the wall-clock
 * times in `hours`. See models/Business.js for why the two differ.
 */
export const setClosure = (token, mapName, zoneId, body) => apiRequest(
  `/api/businesses/${mapName}/${zoneId}/closure`,
  { method: 'POST', token, body },
);

/** Back to "always open, nothing wrong" — the row goes away entirely. */
export const clearBusiness = (token, mapName, zoneId) => apiRequest(
  `/api/businesses/${mapName}/${zoneId}`,
  { method: 'DELETE', token },
);

/**
 * The same decision, for every building on a world at once.
 *
 * ONE REQUEST, not one per building. A district can carry a hundred
 * storefronts, and looping `setClosure` would be a hundred round trips, a
 * hundred full collection reads on the server and a hundred broadcasts to
 * everybody standing in that world — to communicate one decision.
 *
 * `onlyUnauthored` defaults to TRUE on the server: a bulk write skips any
 * building somebody has already given real hours or a real closure to. Pass
 * `false` only when replacing that is what you mean.
 *
 * @param {object} body `{ closure, kinds?, zoneIds?, onlyUnauthored?, note? }`
 */
export const setClosureBulk = (token, mapName, body) => apiRequest(
  `/api/businesses/${mapName}/closure`,
  { method: 'POST', token, body },
);
